import React from "react";
import { Platform, View } from "react-native";
import { Surface, Text, Button } from "react-native-paper";
import Animated, { FadeInDown, FadeOutUp } from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAppContext } from "../../Universal/API/Context-API/AppContext";
import { webFont } from "../../Universal/API/API";

export type NotificationKind = "success" | "error" | "info";

type NotificationPayload = {
  title: string;
  message: string;
  kind?: NotificationKind;
  duration?: number;
};

type NotificationItem = NotificationPayload & {
  id: string;
};

type NotificationsContextValue = {
  showNotification: (payload: NotificationPayload) => void;
  notifySuccess: (title: string, message: string, duration?: number) => void;
  notifyError: (title: string, message: string, duration?: number) => void;
  notifyInfo: (title: string, message: string, duration?: number) => void;
};

const DEFAULT_DURATION = 4200;

const NotificationsContext = React.createContext<NotificationsContextValue | undefined>(undefined);

const paletteByKind: Record<
  NotificationKind,
  {
    backgroundColor: string;
    textColor: string;
    titleColor: string;
  }
> = {
  success: {
    backgroundColor: "#183E26",
    textColor: "rgba(255,255,255,0.86)",
    titleColor: "#FFFFFF",
  },
  error: {
    backgroundColor: "#4A1F18",
    textColor: "rgba(255,255,255,0.86)",
    titleColor: "#FFFFFF",
  },
  info: {
    backgroundColor: "#111111",
    textColor: "rgba(255,255,255,0.86)",
    titleColor: "#FFFFFF",
  },
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { scaleAll, isSmallVersion } = useAppContext();
  const insets = useSafeAreaInsets();
  const queueRef = React.useRef<NotificationItem[]>([]);
  const [current, setCurrent] = React.useState<NotificationItem | null>(null);
  const [visible, setVisible] = React.useState(false);

  const pumpQueue = React.useCallback(() => {
    if (visible) {
      return;
    }

    const next = queueRef.current.shift() ?? null;
    if (!next) {
      setCurrent(null);
      return;
    }

    setCurrent(next);
    setVisible(true);
  }, [visible]);

  const showNotification = React.useCallback(
    ({ title, message, kind = "info", duration = DEFAULT_DURATION }: NotificationPayload) => {
      const next: NotificationItem = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        title,
        message,
        kind,
        duration,
      };

      if (!current && !visible) {
        setCurrent(next);
        setVisible(true);
        return;
      }

      queueRef.current.push(next);
    },
    [current, visible]
  );

  const notifySuccess = React.useCallback(
    (title: string, message: string, duration?: number) => {
      showNotification({ title, message, kind: "success", duration });
    },
    [showNotification]
  );

  const notifyError = React.useCallback(
    (title: string, message: string, duration?: number) => {
      showNotification({ title, message, kind: "error", duration });
    },
    [showNotification]
  );

  const notifyInfo = React.useCallback(
    (title: string, message: string, duration?: number) => {
      showNotification({ title, message, kind: "info", duration });
    },
    [showNotification]
  );

  const handleDismiss = React.useCallback(() => {
    setVisible(false);
  }, []);

  React.useEffect(() => {
    if (!visible || !current) {
      return;
    }

    const timeout = setTimeout(() => {
      setVisible(false);
    }, current.duration ?? DEFAULT_DURATION);

    return () => clearTimeout(timeout);
  }, [current, visible]);

  React.useEffect(() => {
    if (visible || !current) {
      return;
    }

    const timeout = setTimeout(() => {
      pumpQueue();
    }, 180);

    return () => clearTimeout(timeout);
  }, [current, pumpQueue, visible]);

  const colors = paletteByKind[current?.kind ?? "info"];
  const AnimatedView = Animated.createAnimatedComponent(View);

  return (
    <NotificationsContext.Provider
      value={{
        showNotification,
        notifySuccess,
        notifyError,
        notifyInfo,
      }}
    >
      <View style={{ flex: 1 }}>
        {children}
        {visible && current ? (
          <View
            pointerEvents="box-none"
            style={{
              position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
              left: 0,
              right: 0,
              top: 18 * scaleAll + insets.top,
              alignItems: "center",
              paddingHorizontal: 14 * scaleAll,
              zIndex: 9999,
              elevation: 9999,
            }}
          >
            <AnimatedView
              key={current.id}
              entering={FadeInDown.duration(220)}
              exiting={FadeOutUp.duration(220)}
              style={{
                width: isSmallVersion ? "100%" : 420 * scaleAll,
                maxWidth: "100%",
              }}
            >
              <Surface
                elevation={5}
                style={{
                  borderRadius: 22 * scaleAll,
                  backgroundColor: colors.backgroundColor,
                  paddingHorizontal: 16 * scaleAll,
                  paddingVertical: 14 * scaleAll,
                }}
              >
                <View style={{ flexDirection: "row", alignItems: "flex-start" }}>
                  <View style={{ flex: 1, paddingRight: 10 * scaleAll }}>
                    <Text
                      selectable={false}
                      style={{
                        color: colors.titleColor,
                        fontFamily: webFont("Inter"),
                        fontSize: 14 * scaleAll,
                        fontWeight: "700",
                        marginBottom: 4 * scaleAll,
                      }}
                    >
                      {current.title}
                    </Text>
                    <Text
                      selectable={false}
                      style={{
                        color: colors.textColor,
                        fontFamily: webFont("Inter"),
                        fontSize: 13 * scaleAll,
                        fontWeight: "500",
                        lineHeight: 19 * scaleAll,
                      }}
                    >
                      {current.message}
                    </Text>
                  </View>

                  <Button
                    mode="text"
                    compact={true}
                    textColor="#FFFFFF"
                    onPress={handleDismiss}
                    contentStyle={{ minHeight: 32 * scaleAll }}
                    labelStyle={{
                      fontFamily: webFont("Inter"),
                      fontSize: 12 * scaleAll,
                      fontWeight: "700",
                    }}
                  >
                    Закрыть
                  </Button>
                </View>
              </Surface>
            </AnimatedView>
          </View>
        ) : null}
      </View>
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = React.useContext(NotificationsContext);

  if (!context) {
    throw new Error("useNotifications must be used within NotificationsProvider");
  }

  return context;
};
